import { memo, SyntheticEvent, useMemo } from "react";
import { Form, FormGroup, FormField, Dropdown, Icon } from "semantic-ui-react";

// Components
import TableMemberGroup from "./TableMemberGroup";

// Commons
import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";
import PaginationShorthand from "react-lib/appcon/common/PaginationShorthand";
import { MeCastMemberGroupTab, MemberGroupListNodesItem, MemberGroupListParams, PickedState, SelectedMemberNode, SetProp } from "./MeCastType";
import { BTN_ACTS, DEFAULT_MEMBER_GROUP_LIST_LIMIT } from "./sequence/MeCast";
import DiagRuleSerializer from "issara-sdk/types/DiagRuleSerializer_apps_PRX";
import PublishedDiagRuleNodeSerializer from "issara-sdk/types/PublishedDiagRuleNodeSerializer_apps_PRX";
import { OptionType } from "../HISV3/common/CommonInterface";
import PublishedDiagRuleWithNodeSerializer from "@/issara-sdk/types/PublishedDiagRuleWithNodeSerializer_apps_PRX";

type CardMeCastMemberGroupProps = {
  // data
  memberGroupTab?: MeCastMemberGroupTab;
  // options
  diagFormList?: DiagRuleSerializer[];
  pdrNodeList?: PublishedDiagRuleNodeSerializer[];
  // actions
  onOpenMemberNode: (group: PublishedDiagRuleWithNodeSerializer, node: MemberGroupListNodesItem) => void;
  // search
  filter: MemberGroupListParams;
  latestRefresh: string;
  memberGroupListParams: MemberGroupListParams;
  onChangeData: (e: SyntheticEvent | null, data: any) => void;
  onChangeDate: (name: string, date: string) => void;
  onRefresh: () => void;
  onSearch: () => void;
  // pagination
  activePage: number;
  onChangePage: (page: number) => void;
  // infra
  setProp: SetProp;
} & Pick<
  PickedState, "buttonLoadCheck"
>;

const CardMeCastMemberGroup = (props: CardMeCastMemberGroupProps) => {

  const formattedDiagFormOptions: OptionType[] = useMemo(() => {
    return (props.diagFormList || []).map(item => ({
      key: item.id as number,
      value: item.name,
      text: item.name
    }));
  }, [props.diagFormList]);

  const formattedGroupNameOptions: OptionType[] = useMemo(() => {
    return (props.pdrNodeList || []).map(item => ({
      key: item.id as number,
      value: item.node_name,
      text: item.node_name,
    }));
  }, [props.pdrNodeList]);

  return (
    <div>
      {/* --- Filter Section --- */}
      <Form>
        <FormGroup inline>
          <FormField width={3}>
            <div style={{ width: "100%" }}>
              <Dropdown
                name="diag_rule_name"
                selection
                fluid
                clearable
                options={formattedDiagFormOptions}
                placeholder="Search by Form Name"
                onChange={props.onChangeData}
              />
            </div>
          </FormField>
          
          <FormField width={3}>
            <div style={{ width: "100%" }}>
              <Dropdown
                name="node_name"
                selection
                fluid
                clearable
                options={formattedGroupNameOptions}
                placeholder="Search by Group Name"
                onChange={props.onChangeData}
              />
            </div>
          </FormField>

          <FormField>
            <div style={{ width: "100%" }}>
              <ButtonLoadCheck
                setProp={props.setProp}
                paramKey={BTN_ACTS.GET_MEMBER_GROUP_LIST}
                buttonLoadCheck={props.buttonLoadCheck?.[`${BTN_ACTS.GET_MEMBER_GROUP_LIST}`]}
                onClick={props.onSearch}
                style={{
                  background: "var(--primary-theme-color)",
                  color: "white"
                }}
                title={
                  <div
                    style={{
                      display: "flex",
                      gap: "8px",
                      alignContent: "center"
                    }}
                  >
                    <label style={{ cursor: "pointer" }}>Search</label>
                    <Icon name="search" />
                  </div>
                }
              />
            </div>
          </FormField>
        </FormGroup>
        <FormGroup>
          <FormField>
            <div style={{ width: "100%" }}>
              {props.buttonLoadCheck?.[`${BTN_ACTS.GET_MEMBER_GROUP_LIST}`] ? (
                <Icon name="spinner" loading />
              ) : (
                <Icon name="redo" style={{ cursor: "pointer" }} onClick={props.onRefresh} />
              )}
              <label style={{ fontStyle: "italic" }}>Last Updated: {props.latestRefresh}</label>
            </div>
          </FormField>
        </FormGroup>
      </Form>

      {/* --- Table Section --- */}
      <TableMemberGroup
        data={props.memberGroupTab?.memberGroupList?.items || []}
        onEditNode={props.onOpenMemberNode}
      />

      {/* --- Pagination --- */}
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "8px" }}>
        <PaginationShorthand
          activePage={props.activePage || 1}
          limit={DEFAULT_MEMBER_GROUP_LIST_LIMIT}
          total={props.memberGroupTab?.memberGroupList?.total}
          onPageChange={props.onChangePage}
        />
      </div>
    </div>
  );
};

CardMeCastMemberGroup.displayName = "CardMeCastMemberGroup";

export default memo(CardMeCastMemberGroup);